import { useEffect, useState } from "react";
import { Loader2, Calendar, UserPlus, Check } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { PageHeader, StatCard } from "@/components/common/ui";
import { cn } from "@/lib/utils";

export default function Staffing() {
  const [shifts, setShifts] = useState(null);
  const [staff, setStaff] = useState([]);
  const [picks, setPicks] = useState({});

  const load = () => api.get("/shifts").then((r) => setShifts(r.data)).catch(() => setShifts([]));
  useEffect(() => {
    load();
    api.get("/staff").then((r) => setStaff(r.data)).catch(() => {});
  }, []);

  const assign = async (id) => {
    const name = picks[id];
    if (!name) { toast.error("Select a staff member first"); return; }
    try { await api.post(`/shifts/${id}/assign`, { staff_name: name }); toast.success("Shift assigned"); load(); }
    catch (e) { toast.error("Assignment failed"); }
  };

  if (!shifts) return <div className="flex h-64 items-center justify-center text-muted-foreground"><Loader2 className="animate-spin" /></div>;

  const byDate = shifts.reduce((a, s) => { (a[s.date] = a[s.date] || []).push(s); return a; }, {});
  const dates = Object.keys(byDate).sort();
  const open = shifts.filter((s) => s.status === "open").length;
  const filled = shifts.filter((s) => s.status === "filled").length;
  const coverage = Math.round((filled / shifts.length) * 100);

  return (
    <div>
      <PageHeader overline="Workforce · AI Scheduling" title="Smart Staffing"
        description="Acuity-aware coverage with AI forecasting. All recommendations are explainable and manager-reviewable." />

      <div className="mb-6 grid grid-cols-2 gap-4 md:grid-cols-4">
        <StatCard icon={Calendar} label="Coverage" value={`${coverage}%`} accent="#12B76A" index={0} />
        <StatCard icon={UserPlus} label="Open Shifts" value={open} accent="#F04438" index={1} />
        <StatCard icon={Check} label="Filled Shifts" value={filled} accent="#155EEF" index={2} />
        <StatCard icon={UserPlus} label="Active Staff" value={staff.length} accent="#7C3AED" index={3} />
      </div>

      <div className="space-y-5">
        {dates.map((d) => {
          const dayShifts = byDate[d];
          const dayOpen = dayShifts.filter((s) => s.status === "open").length;
          return (
            <div key={d} className="rounded-xl border bg-card shadow-sm">
              <div className="flex items-center justify-between border-b px-4 py-3">
                <div className="font-semibold">{new Date(d).toLocaleDateString(undefined, { weekday: "long", month: "short", day: "numeric" })}</div>
                {dayOpen > 0 ? <span className="rounded-full bg-[#F0443815] px-2.5 py-0.5 text-xs font-semibold text-[#F04438]">{dayOpen} open</span>
                  : <span className="rounded-full bg-[#12B76A15] px-2.5 py-0.5 text-xs font-semibold text-[#12B76A]">Fully covered</span>}
              </div>
              <div className="grid grid-cols-1 divide-y md:grid-cols-3 md:divide-x md:divide-y-0">
                {["Day", "Evening", "Overnight"].map((shiftType) => {
                  const items = dayShifts.filter((s) => s.shift === shiftType);
                  return (
                    <div key={shiftType} className="p-4">
                      <div className="overline mb-2">{shiftType}</div>
                      <div className="space-y-2">
                        {items.map((s) => (
                          <div key={s.id} data-testid={`shift-${s.id}`}
                            className={cn("rounded-lg border p-2.5 text-sm", s.status === "open" ? "border-[#F04438]/40 bg-[#F04438]/5" : "bg-background")}>
                            <div className="flex items-center justify-between">
                              <span className="text-xs text-muted-foreground">{s.program_name}</span>
                              {s.acuity === "High" && <span className="rounded bg-[#F7900915] px-1.5 text-[10px] font-semibold text-[#F79009]">High acuity</span>}
                            </div>
                            {s.status === "filled" ? (
                              <div className="mt-1 flex items-center gap-1.5 font-medium"><Check size={13} className="text-[#12B76A]" />{s.assigned_staff[0]}</div>
                            ) : (
                              <div className="mt-1.5 flex gap-1.5">
                                <select data-testid={`assign-select-${s.id}`} value={picks[s.id] || ""}
                                  onChange={(e) => setPicks((p) => ({ ...p, [s.id]: e.target.value }))}
                                  className="h-8 flex-1 rounded-md border bg-card px-2 text-xs outline-none focus:border-[#155EEF]">
                                  <option value="">Assign staff…</option>
                                  {staff.map((st) => <option key={st.id} value={`${st.first_name} ${st.last_name}`}>{st.first_name} {st.last_name}</option>)}
                                </select>
                                <button data-testid={`assign-btn-${s.id}`} onClick={() => assign(s.id)}
                                  className="rounded-md bg-[#155EEF] px-2.5 text-xs font-semibold text-white transition-colors hover:bg-[#0F4FD1]">Assign</button>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
